export type Align = 'left' | 'center' | 'right';

export type EmailBlock =
  | { id: string; type: 'heading'; text: string; size: 'l' | 'm'; align: Align }
  | { id: string; type: 'text'; html: string; align: Align }
  | { id: string; type: 'image'; src: string; alt: string; href: string; width: number; align: Align }
  | { id: string; type: 'button'; label: string; href: string; align: Align; color: string; size?: ButtonSize; shape?: ButtonShape }
  | { id: string; type: 'divider' }
  | { id: string; type: 'spacer'; height: number };

export type BlockType = EmailBlock['type'];
export type ButtonSize = 'narrow' | 'normal' | 'wide' | 'full';
export type ButtonShape = 'square' | 'rounded' | 'pill';

const BUTTON_PAD: Record<ButtonSize, string> = { narrow: '10px 16px', normal: '12px 22px', wide: '14px 44px', full: '14px 22px' };
const BUTTON_RADIUS: Record<ButtonShape, number> = { square: 0, rounded: 8, pill: 999 };

export const BLOCK_LABEL: Record<BlockType, string> = {
  heading: 'Nadpis',
  text: 'Text',
  image: 'Obrázek',
  button: 'Tlačítko',
  divider: 'Oddělovač',
  spacer: 'Mezera',
};

export const BUTTON_COLORS = ['#4f46e5', '#5c4290', '#111111', '#087c3d', '#e37921'];

const CONTENT_WIDTH = 536;

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function newBlock(type: BlockType): EmailBlock {
  const id = uid();
  switch (type) {
    case 'heading': return { id, type, text: 'Nadpis sekce', size: 'm', align: 'left' };
    case 'text': return { id, type, html: 'Sem vložte obsah e-mailu.', align: 'left' };
    case 'image': return { id, type, src: '', alt: '', href: '', width: 100, align: 'center' };
    case 'button': return { id, type, label: 'Zjistit více', href: 'https://', align: 'left', color: BUTTON_COLORS[0]!, size: 'normal', shape: 'rounded' };
    case 'divider': return { id, type };
    case 'spacer': return { id, type, height: 24 };
  }
}

export function cloneBlock(b: EmailBlock): EmailBlock {
  return { ...b, id: uid() };
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function safeUrl(url: string): string | null {
  const u = url.trim();
  if (/^mailto:[^\s"<>]+$/i.test(u)) return u;
  if (/^\[%\s*shared\.[A-Z_]+\s*%\]$/.test(u)) return u;
  try {
    const p = new URL(u);
    return p.protocol === 'https:' || p.protocol === 'http:' ? p.href : null;
  } catch {
    return null;
  }
}

const ALLOWED = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'A', 'BR', 'P', 'UL', 'OL', 'LI']);
const P_STYLE = 'margin:0 0 16px;font-size:16px;line-height:1.6;color:#111111;';
const LIST_STYLE = 'margin:0 0 16px;padding-left:22px;font-size:16px;line-height:1.6;color:#111111;';
const A_STYLE = 'color:#4f46e5;text-decoration:underline;';

export function sanitizeRich(html: string): string {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;
  const walk = (el: Element) => {
    for (const child of [...el.children]) {
      walk(child);
      if (child.tagName === 'DIV') {
        const p = doc.createElement('p');
        p.innerHTML = child.innerHTML;
        child.replaceWith(p);
        continue;
      }
      if (!ALLOWED.has(child.tagName)) {
        child.replaceWith(...child.childNodes);
        continue;
      }
      const href = child.tagName === 'A' ? safeUrl(child.getAttribute('href') ?? '') : null;
      for (const a of [...child.attributes]) child.removeAttribute(a.name);
      if (child.tagName === 'A') {
        if (!href) { child.replaceWith(...child.childNodes); continue; }
        child.setAttribute('href', href);
      }
    }
  };
  walk(root);
  return root.innerHTML;
}

function styleRich(html: string, align: Align): string {
  const doc = new DOMParser().parseFromString(`<div>${sanitizeRich(html)}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;
  const loose: ChildNode[] = [];
  const out: string[] = [];
  const flush = () => {
    if (!loose.length) return;
    const p = doc.createElement('p');
    loose.splice(0).forEach((n) => p.appendChild(n));
    if (p.textContent?.trim() || p.querySelector('br')) out.push(p.outerHTML);
  };
  for (const n of [...root.childNodes]) {
    if (n.nodeType === 1 && ['P', 'UL', 'OL'].includes((n as Element).tagName)) { flush(); out.push((n as Element).outerHTML); }
    else loose.push(n);
  }
  flush();
  const joined = new DOMParser().parseFromString(`<div>${out.join('')}</div>`, 'text/html').body.firstElementChild as HTMLElement;
  joined.querySelectorAll('p').forEach((p) => p.setAttribute('style', `${P_STYLE}text-align:${align};`));
  joined.querySelectorAll('ul,ol').forEach((l) => l.setAttribute('style', `${LIST_STYLE}text-align:${align};`));
  joined.querySelectorAll('li').forEach((li) => li.setAttribute('style', 'margin:0 0 6px;'));
  joined.querySelectorAll('a').forEach((a) => a.setAttribute('style', A_STYLE));
  return joined.innerHTML;
}

export function renderBlock(b: EmailBlock): string {
  switch (b.type) {
    case 'heading': {
      const size = b.size === 'l' ? 24 : 20;
      return `<p style="margin:0 0 14px;font-size:${size}px;line-height:1.3;font-weight:700;color:#111111;text-align:${b.align};">${esc(b.text)}</p>`;
    }
    case 'text':
      return styleRich(b.html, b.align);
    case 'image': {
      if (!b.src) return '';
      const w = Math.round((CONTENT_WIDTH * Math.min(100, Math.max(20, b.width))) / 100);
      const img = `<img src="${esc(b.src)}" width="${w}" alt="${esc(b.alt)}" style="display:inline-block;border:0;width:100%;max-width:${w}px;height:auto;border-radius:8px;">`;
      const href = b.href ? safeUrl(b.href) : null;
      return `<div style="margin:0 0 16px;text-align:${b.align};">${href ? `<a href="${esc(href)}" style="text-decoration:none;">${img}</a>` : img}</div>`;
    }
    case 'button': {
      const href = safeUrl(b.href) ?? '#';
      const color = /^#[0-9a-fA-F]{6}$/.test(b.color) ? b.color : BUTTON_COLORS[0]!;
      const size = b.size ?? 'normal';
      const radius = BUTTON_RADIUS[b.shape ?? 'rounded'];
      const full = size === 'full';
      const link = `<a href="${esc(href)}" style="display:${full ? 'block' : 'inline-block'};padding:${BUTTON_PAD[size]};font-size:15px;line-height:1;font-weight:600;color:#ffffff;text-align:center;text-decoration:none;border-radius:${radius}px;">${esc(b.label)}</a>`;
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:4px 0 20px;"><tr><td align="${b.align}"><table role="presentation" cellpadding="0" cellspacing="0"${full ? ' width="100%"' : ''}><tr><td bgcolor="${color}" align="center" style="border-radius:${radius}px;background:${color};">${link}</td></tr></table></td></tr></table>`;
    }
    case 'divider':
      return '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:8px 0 24px;"><tr><td style="height:1px;line-height:1px;font-size:1px;background:#e3e6ea;">&nbsp;</td></tr></table>';
    case 'spacer':
      return `<div style="height:${Math.min(120, Math.max(4, b.height))}px;line-height:${Math.min(120, Math.max(4, b.height))}px;font-size:1px;">&nbsp;</div>`;
  }
}

export function renderBlocks(blocks: EmailBlock[]): string {
  return blocks.map(renderBlock).filter(Boolean).join('\n');
}

export function hasEditableRegion(html: string): boolean {
  return /data-editable=/.test(html);
}

export function applyBlocks(templateHtml: string, blocks: EmailBlock[]): string {
  const doc = new DOMParser().parseFromString(templateHtml, 'text/html');
  const region = doc.querySelector('[data-editable]');
  if (!region) return templateHtml;
  region.innerHTML = renderBlocks(blocks);
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

export function blockIssues(blocks: EmailBlock[]): string[] {
  const out: string[] = [];
  if (!blocks.length) out.push('Přidej do e-mailu aspoň jeden blok.');
  blocks.forEach((b, i) => {
    const n = `Blok ${i + 1} (${BLOCK_LABEL[b.type]})`;
    if (b.type === 'button' && !safeUrl(b.href)) out.push(`${n}: odkaz tlačítka musí být platná adresa https://…`);
    if (b.type === 'image' && !b.src) out.push(`${n}: nahraj obrázek.`);
    if (b.type === 'image' && b.href && !safeUrl(b.href)) out.push(`${n}: odkaz obrázku není platná adresa.`);
  });
  return out;
}
